import React from 'react';
import { Row, Col, Input, Select, DatePicker } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';

type FilterOption = { value: string | number; label: string };

interface FilterOptions {
  nations: FilterOption[];
  payment_statuses: FilterOption[];
  applicant_names: FilterOption[];
  contact_names: FilterOption[];
  channels: FilterOption[];
  sources: FilterOption[];
  statuses: FilterOption[];
}

interface FiltersState {
  nation: string;
  payment_status: string;
  applicant_name: string;
  contact_name: string;
  channel: string;
  source: string;
  status: string;
  seller?: string;
  date_field: string;
  date_from: string;
  date_to: string;
}

interface OrdersToolbarProps {
  searchText: string;
  searchField: string;
  searchFieldOptions: FilterOption[];
  onSearchTextChange: (text: string) => void;
  onSearchFieldChange: (value: string) => void;
  filterOptions: FilterOptions;
  filters: FiltersState;
  onFilterChange: (key: keyof FiltersState, value: string) => void;
  serviceFilter?: string;
  // Admin-only seller filter
  isAdmin?: boolean;
  sellerOptions?: FilterOption[];
}

const OrdersToolbar: React.FC<OrdersToolbarProps> = ({
  searchText,
  searchField,
  searchFieldOptions,
  onSearchTextChange,
  onSearchFieldChange,
  filterOptions,
  filters,
  onFilterChange,
  isAdmin,
  sellerOptions = [],
}) => {

  const dateRangeValue: [Dayjs, Dayjs] | null = (filters.date_from && filters.date_to)
    ? [dayjs(filters.date_from), dayjs(filters.date_to)]
    : null;

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      <Col span={5}>
        <Input.Group compact>
          <Select
            style={{ width: '35%' }}
            value={searchField}
            onChange={(value) => onSearchFieldChange(value)}
            data-testid="orders-search-field"
          >
            {searchFieldOptions.map(option => (
              <Select.Option key={String(option.value)} value={String(option.value)}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
          <Input
            style={{ width: '65%' }}
            placeholder="搜索订单编号、案件号、申请人"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => onSearchTextChange(e.target.value)}
            data-testid="orders-search"
          />
        </Input.Group>
      </Col>

      <Col span={18}>
        <Row gutter={[12, 16]} style={{ marginBottom: 16 }}>
          <Col span={2}>
            <Select
              placeholder="国家"
              style={{ width: '100%' }}
              value={filters.nation || undefined}
              onChange={(value) => onFilterChange('nation', value || '')}
              allowClear
              showSearch
              data-testid="orders-filter-nation"
            >
              {filterOptions.nations.map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={2}>
            <Select
              placeholder="付款状态"
              style={{ width: '100%' }}
              value={filters.payment_status || undefined}
              onChange={(value) => onFilterChange('payment_status', value || '')}
              allowClear
              showSearch
              data-testid="orders-filter-payment"
            >
              {filterOptions.payment_statuses.map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={2}>
            <Select
              placeholder="申请人"
              style={{ width: '100%' }}
              value={filters.applicant_name || undefined}
              onChange={(value) => onFilterChange('applicant_name', value || '')}
              allowClear
              showSearch
              data-testid="orders-filter-applicant"
            >
              {filterOptions.applicant_names.map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={2}>
            <Select
              placeholder="客户名称"
              style={{ width: '100%' }}
              value={filters.contact_name || undefined}
              onChange={(value) => onFilterChange('contact_name', value || '')}
              allowClear
              data-testid="orders-filter-contact"
              showSearch
              optionFilterProp="label"
            >
              {filterOptions.contact_names.map(option => (
                <Select.Option key={option.value} value={option.value} label={option.label}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={2}>
            <Select
              placeholder="渠道"
              style={{ width: '100%' }}
              value={filters.channel || undefined}
              onChange={(value) => onFilterChange('channel', value || '')}
              allowClear
              showSearch
              data-testid="orders-filter-channel"
            >
              {filterOptions.channels.map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={2}>
            <Select
              placeholder="来源"
              style={{ width: '100%' }}
              value={filters.source || undefined}
              onChange={(value) => onFilterChange('source', value || '')}
              allowClear
              showSearch
              data-testid="orders-filter-source"
            >
              {filterOptions.sources.map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={3}>
            <Select
              placeholder="最新状态"
              style={{ width: '100%' }}
              value={filters.status || undefined}
              onChange={(value) => onFilterChange('status', value || '')}
              allowClear
              showSearch
              data-testid="orders-filter-status"
            >
              {filterOptions.statuses.map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Col>
          {isAdmin && (
            <Col span={2}>
              <Select
                placeholder="所属销售"
                style={{ width: '100%' }}
                value={filters.seller || undefined}
                onChange={(value) => onFilterChange('seller', value ? String(value) : '')}
                allowClear
                data-testid="orders-filter-seller"
                showSearch
                optionFilterProp="label"
              >
                {sellerOptions.map(option => (
                  <Select.Option key={String(option.value)} value={String(option.value)} label={option.label}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </Col>
          )}
          <Col span={2}>
            <Select
              placeholder="日期类型"
              style={{ width: '100%' }}
              value={filters.date_field || undefined}
              onChange={(value) => onFilterChange('date_field', value || '')}
              allowClear
            >
              <Select.Option value="payment_at">付款日期</Select.Option>
              <Select.Option value="approve_at">注册日期</Select.Option>
              <Select.Option value="announcement_at">公告日期</Select.Option>
              <Select.Option value="decline_at">驳回日期</Select.Option>
              <Select.Option value="receipt_at">回执日期</Select.Option>
            </Select>
          </Col>
          <Col span={4}>
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              value={dateRangeValue as any}
              onChange={(dates) => {
                const start = dates && dates[0] ? dates[0].format('YYYY-MM-DD') : '';
                const end = dates && dates[1] ? dates[1].format('YYYY-MM-DD') : '';
                onFilterChange('date_from', start);
                onFilterChange('date_to', end);
              }}
              allowEmpty={[true, true]}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default OrdersToolbar;
